import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { authAPI, usersAPI } from '../services/api';
import { Card, Button, PageHeader, PasswordInput } from '../components/common';
import { useAuth } from '../context/AuthContext';
import { useNotificari } from '../hooks/useNotificari';
import { formatDataOra, ROL_LABELS } from '../utils';
import Layout from '../components/layout/Layout';

export default function ProfilPage() {
  const { user } = useAuth();
  const { notificari, necitite, markRead, markAllRead } = useNotificari();
  const [tab, setTab] = useState('profil');

  return (
    <Layout>
      <PageHeader title="Profilul meu" />

      <div style={tabs}>
        {[['profil','Profil'],['parola','Schimbă parola'],['notificari',`Notificări${necitite > 0 ? ` (${necitite})` : ''}`]].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{ ...tabBtn, ...(tab === key ? tabActive : {}) }}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'profil'      && <TabProfil    user={user} />}
      {tab === 'parola'      && <TabParola />}
      {tab === 'notificari'  && <TabNotificari notificari={notificari} markRead={markRead} markAllRead={markAllRead} necitite={necitite} />}
    </Layout>
  );
}

// ── Tab Profil ────────────────────────────────────────────────
function TabProfil({ user: userCtx }) {
  const { isFunctionar } = useAuth();
  const queryClient = useQueryClient();

  const { data: meData } = useQuery(
    'profile-me',
    () => usersAPI.me().then(r => r.data.data),
    { staleTime: 30000 }
  );
  const user = meData || userCtx;

  const { register, handleSubmit, reset } = useForm({
    defaultValues: { phone: '', adresa: '' }
  });

  useEffect(() => {
    if (meData) {
      reset({ phone: meData.phone || '', adresa: meData.adresa || '' });
    }
  }, [meData, reset]);

  const mutation = useMutation(
    (data) => usersAPI.updateMe(data),
    {
      onSuccess: () => {
        toast.success('Profil actualizat cu succes!');
        queryClient.invalidateQueries('profile-me');
      },
      onError: (err) => {
        const msg = err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Eroare la salvare';
        toast.error(msg);
      },
    }
  );

  const randuri = [
    ['Nume complet',  `${user?.prenume} ${user?.nume}`],
    ['Email',         user?.email],
    ['Rol',           ROL_LABELS[user?.rol] || user?.rol],
    ...(isFunctionar ? [['Departament', user?.departament || '—']] : []),
    ['Ultimul login', formatDataOra(user?.last_login)],
  ];

  return (
    <Card title="Informații cont">
      <div style={infoGrid}>
        {randuri.map(([k, v]) => (
          <div key={k} style={infoRow}>
            <span style={infoKey}>{k}:</span>
            <span style={infoVal}>{v || '—'}</span>
          </div>
        ))}
      </div>

      <hr style={{ border: 'none', borderTop: '1px solid #f0f0f0', margin: '20px 0' }} />

      <form onSubmit={handleSubmit(d => mutation.mutate(d))} style={formCol}>
        <div style={field}>
          <label style={label}>Telefon</label>
          <input type="tel" style={input} placeholder="07xx xxx xxx" {...register('phone')} />
        </div>
        <div style={field}>
          <label style={label}>Adresă</label>
          <input type="text" style={input} placeholder="Str. ..." {...register('adresa')} />
        </div>
        <Button type="submit" disabled={mutation.isLoading}>
          {mutation.isLoading ? 'Se salvează...' : 'Salvează modificările'}
        </Button>
      </form>
    </Card>
  );
}

// ── Tab Parola ────────────────────────────────────────────────
function TabParola() {
  const { register, handleSubmit, watch, reset, formState: { errors } } = useForm();
  const pass = watch('newPassword', '');

  const reqs = [
    { label: 'Minim 8 caractere',   met: pass.length >= 8 },
    { label: 'O literă mare',       met: /[A-Z]/.test(pass) },
    { label: 'O literă mică',       met: /[a-z]/.test(pass) },
    { label: 'O cifră',             met: /\d/.test(pass) },
    { label: 'Caracter special',    met: /[@$!%*?&]/.test(pass) },
  ];
  const allMet = reqs.every(r => r.met);

  const mutation = useMutation(
    (d) => authAPI.changePassword(d),
    {
      onSuccess: () => { toast.success('Parolă schimbată cu succes!'); reset(); },
      onError: (err) => toast.error(err.response?.data?.message || 'Eroare schimbare parolă'),
    }
  );

  return (
    <Card title="Schimbare parolă">
      <form onSubmit={handleSubmit(d => mutation.mutate(d))} style={formCol}>
        <div style={field}>
          <label style={label}>Parola curentă *</label>
          <PasswordInput style={input} autoComplete="current-password"
            {...register('currentPassword', { required: 'Parola curentă este obligatorie' })} />
          {errors.currentPassword && <span style={errMsg}>{errors.currentPassword.message}</span>}
        </div>

        <div style={field}>
          <label style={label}>Parola nouă *</label>
          <PasswordInput style={input} autoComplete="new-password"
            {...register('newPassword', {
              required: 'Parola nouă este obligatorie',
              validate: v => allMet || 'Parola nu îndeplinește cerințele'
            })} />
          <div style={reqsBox}>
            {reqs.map(r => (
              <div key={r.label} style={{ fontSize: '12px', color: r.met ? '#3b6d11' : '#aaa', padding: '2px 0' }}>
                {r.met ? '✓' : '○'} {r.label}
              </div>
            ))}
          </div>
        </div>

        <div style={field}>
          <label style={label}>Confirmă parola nouă *</label>
          <PasswordInput style={input} autoComplete="new-password"
            {...register('confirmPassword', {
              required: 'Confirmarea este obligatorie',
              validate: v => v === pass || 'Parolele nu coincid'
            })} />
          {errors.confirmPassword && <span style={errMsg}>{errors.confirmPassword.message}</span>}
        </div>

        <Button type="submit" disabled={mutation.isLoading || !allMet}>
          {mutation.isLoading ? 'Se salvează...' : 'Schimbă parola'}
        </Button>
      </form>
    </Card>
  );
}

// ── Tab Notificări ────────────────────────────────────────────
function TabNotificari({ notificari, markRead, markAllRead, necitite }) {
  return (
    <Card
      title={`Notificări (${necitite} necitite)`}
      action={necitite > 0 && <button onClick={markAllRead} style={markAllBtn}>Marchează toate citite</button>}
    >
      {notificari.length === 0
        ? <p style={{ color: '#888', textAlign: 'center', padding: '32px' }}>Nicio notificare</p>
        : notificari.map(n => (
          <div key={n.id} style={{ ...notifRow, ...(n.citita ? {} : notifUnread) }}>
            <div style={notifContent}>
              <div style={notifTitle}>{n.titlu}</div>
              <div style={notifMsg}>{n.mesaj}</div>
              <div style={notifDate}>{formatDataOra(n.created_at)}</div>
            </div>
            {!n.citita && (
              <button onClick={() => markRead(n.id)} style={markBtn}>✓</button>
            )}
          </div>
        ))
      }
    </Card>
  );
}

const tabs       = { display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '2px solid #f0f0f0', paddingBottom: '0', overflowX: 'auto', WebkitOverflowScrolling: 'touch' };
const tabBtn     = { padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '500', color: '#888', borderBottom: '2px solid transparent', marginBottom: '-2px', whiteSpace: 'nowrap', flexShrink: 0 };
const tabActive  = { color: '#2d2d7e', borderBottomColor: '#2d2d7e' };
const infoGrid   = { display: 'grid', gap: '0' };
const infoRow    = { display: 'flex', gap: '12px', padding: '10px 0', borderBottom: '1px solid #f5f5f5', fontSize: '14px' };
const infoKey    = { color: '#888', minWidth: '120px', flexShrink: 0 };
const infoVal    = { color: '#1a1a2e', fontWeight: '500' };
const formCol    = { display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '400px' };
const field      = { display: 'flex', flexDirection: 'column', gap: '6px' };
const label      = { fontSize: '13px', fontWeight: '500', color: '#555' };
const input      = { padding: '10px 14px', fontSize: '16px', border: '1.5px solid #ddd', borderRadius: '8px', outline: 'none', width: '100%', boxSizing: 'border-box' };
const errMsg     = { fontSize: '12px', color: '#e24b4a' };
const reqsBox    = { padding: '10px 12px', backgroundColor: '#f9f9f9', borderRadius: '6px', marginTop: '4px' };
const notifRow   = { display: 'flex', gap: '12px', padding: '14px 0', borderBottom: '1px solid #f5f5f5', alignItems: 'flex-start' };
const notifUnread= { backgroundColor: '#f0f4ff', borderRadius: '8px', padding: '12px', marginBottom: '4px' };
const notifContent= { flex: 1 };
const notifTitle = { fontWeight: '600', fontSize: '14px', color: '#1a1a2e', marginBottom: '4px' };
const notifMsg   = { fontSize: '13px', color: '#555', lineHeight: '1.5' };
const notifDate  = { fontSize: '11px', color: '#aaa', marginTop: '6px' };
const markBtn    = { padding: '6px 10px', backgroundColor: '#3b6d11', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', flexShrink: 0 };
const markAllBtn = { padding: '6px 12px', background: 'none', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', color: '#555' };
